import axios from 'axios';
import { API_CONFIG, HTTP_STATUS } from './api.config';
import { Sentry } from '../config/sentry';

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

let onUnauthorized = null;

export const setUnauthorizedCallback = (callback) => {
  onUnauthorized = callback;
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      const isLoginRequest = error.config?.url?.includes('/Auth/Login');
      if (!isLoginRequest) {
        if (onUnauthorized) {
          onUnauthorized();
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
    
    // Reintentar en caso de errores de red
    if (!config || !config.retry) {
      config.retry = 0;
    }
    
    const isMultipart = config.headers?.['Content-Type'] === 'multipart/form-data' ||
      config.data instanceof FormData;

    const shouldRetry = !isMultipart && (
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK' ||
      error.message.includes('Network Error') ||
      error.message.includes('timeout')
    ) && config.retry < API_CONFIG.RETRY_ATTEMPTS;
    
    if (shouldRetry) {
      config.retry += 1;
      const delay = config.retry * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      return api(config);
    }
    
    const errorMessage = error.response?.data?.message || error.message || 'Error en la petición';
    Sentry.captureException(error, {
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: errorMessage,
      },
    });
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.get('/Auth/Login', { params: credentials }),
};

export const turnosAPI = {
  getTurnos: (body) => api.post('/Turnos/ListarTurnos', body),
  getTodosTurnos: (body) => api.post('/Turnos/ListarTodosTurnos', body),
  getTurnosCreados: (body) => api.post('/Turnos/ListarTurnosCreados', body),
  getTurnoById: (id) => api.get(`/Turnos/${id}`),
  guardarTurno: (formData) => api.post('/Turnos/GuardarTurno', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  editarTurno: (id, formData) => api.put(`/Turnos/EditarTurno/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTurno: (id) => api.delete(`/Turnos/${id}`),
  updateEstadoYProcesadorDatosPracticas: (id, data) => api.put(`/Turnos/ActualizarEstadoYProcesadorDatosPracticas/${id}`, data),
  getEstadosSupervisor: (filtrarPorRol = false) => api.get('/Turnos/EstadosSupervisor', { params: { filtrarPorRol } }),
  getEstadosTurno: (idTurno) => api.get(`/Turnos/EstadosTurno/${idTurno}`),
  getEstudiosAnteriores: (idTurno) => api.get(`/Turnos/EstudiosAnteriores/${idTurno}`),
  getEspecialistaDerivador: (idDerivador) => api.get(`/Derivadores/${idDerivador}`),
  getProcesadores: () => api.get('/Procesadores/ListarProcesadores'),
  getMedicos: () => api.get('/Medicos/ListarMedicos'),
  getDerivadores: () => api.get('/Derivadores/ListarDerivadores'),
  calcularFechaEntrega: (fechaTurno, idTipoEstudio) => api.get('/Turnos/CalcularFechaEntrega', { params: { fechaTurno, idTipoEstudio } }),
  generarAccesoDirecto: (idTurno) => api.post('/Auth/GenerarAccesoDirecto', { idTurno }),
};

export const centrosAPI = {
  getCentros: () => api.get('/Centros/ObtenerCentros'),
  guardar: (data) => api.post('/Centros', data),
  actualizar: (id, data) => api.put(`/Centros/${id}`, data),
  eliminar: (id) => api.delete(`/Centros/${id}`),
};

export const tiposEstudiosAPI = {
  getTiposEstudios: () => api.get('/TiposEstudios/ObtenerTiposEstudios'),
  getTiposEstudiosExt: () => api.get('/TiposEstudios/ObtenerTiposEstudiosExt'),
  getTiposEstudiosPorObraSocial: (idObraSocial) => api.get(`/TiposEstudios/ObtenerTiposEstudiosPorObraSocial/${idObraSocial}`),
  getCategorias: () => api.get('/Categorias'),
  guardarTipoEstudio: (data) => api.post('/TiposEstudios', data),
  actualizarTipoEstudio: (id, data) => api.put(`/TiposEstudios/${id}`, data),
  eliminarTipoEstudio: (id) => api.delete(`/TiposEstudios/${id}`),
  // Canales que puede usar cada tipo de estudio: [{ idTipoEstudio, canales, conCpap, etiqueta }]
  getCanalesTiposEstudio: () => api.get('/TiposEstudios/Canales'),
  // Reemplaza los canales del tipo de estudio: canales = [{ canales, conCpap }]
  guardarCanalesTipoEstudio: (idTipoEstudio, canales) => api.put(`/TiposEstudios/${idTipoEstudio}/Canales`, canales),
  guardarTipoEstudioExt: (tipoEstudioExt) => api.post('/TiposEstudios/GuardarTipoEstudioExt', { TipoEstudioExt: tipoEstudioExt }),
  actualizarTipoEstudioExt: (id, tipoEstudioExt) => api.put(`/TiposEstudios/ActualizarTipoEstudioExt/${id}`, { TipoEstudioExt: tipoEstudioExt }),
  eliminarTipoEstudioExt: (id) => api.delete(`/TiposEstudios/EliminarTipoEstudioExt/${id}`),
};

export const procesadoresAPI = {
  getProcesadores: () => api.get('/Procesadores/ListarProcesadores'),
};

export const medicosAPI = {
  getMedicos: () => api.get('/Medicos/ObtenerMedicos'),
  getTecnicos: () => api.get('/Medicos/ObtenerTecnicos'),
  getUsuariosPorRol: (idRol) => api.get('/Medicos/ObtenerUsuariosPorRol', { params: { idRol } }),
  guardarMedico: (nombre, idUsuario) => api.post('/Medicos', { Nombre: nombre, IdUsuario: idUsuario, TipoMedico: 1 }),
  actualizarMedico: (id, nombre, idUsuario) => api.put(`/Medicos/${id}`, { Nombre: nombre, IdUsuario: idUsuario, TipoMedico: 1 }),
  eliminarMedico: (id) => api.delete(`/Medicos/${id}`),
  // Los tipos de estudio ya no se envían desde Técnicos: se cargan por horario (con su cantidad).
  guardarTecnico: (nombre, idUsuario) => api.post('/Medicos', { Nombre: nombre, IdUsuario: idUsuario, TipoMedico: 2 }),
  actualizarTecnico: (id, nombre, idUsuario) => api.put(`/Medicos/${id}`, { Nombre: nombre, IdUsuario: idUsuario, TipoMedico: 2 }),
  eliminarTecnico: (id) => api.delete(`/Medicos/${id}`),
  // Opciones de canal de la sede según sus equipos: [{ canales, conCpap, etiqueta, equipos }]
  getOpcionesCanales: (idCentro) => api.get('/Medicos/OpcionesCanales', { params: idCentro ? { idCentro } : {} }),
  // Alta unitaria: { IdCentro, TipoMedico, IdMedico, HoraDesde, MinutosDesde, DiaSemana, Observacion }
  agregarHorarioMedico: (data) => api.post('/Medicos/AgregarHorarioMedico', data),
  // Alta múltiple: { IdCentro, TipoMedico, IdMedico, HoraDesde, MinutosDesde, Dias, Observacion }
  agregarHorariosMedicos: (data) => api.post('/Medicos/AgregarHorariosMedicos', data),
  // Lista todos los horarios del centro (o de todos si idCentro es null/-1), sin rango de fechas
  listarHorariosMedicos: (idCentro) => api.get('/Medicos/ListarHorariosMedicos', { params: { ...(idCentro && { idCentro }) } }),
  // Devuelve { IdHorarioMedico, IdMedico, IdCentro, TipoMedico, DiaSemana, HoraDesde ("HH:mm" o null), Observacion }
  obtenerHorarioMedico: (id) => api.get(`/Medicos/ObtenerHorarioMedico/${id}`),
  // Actualización: { IdHorarioMedico, IdCentro, IdMedico, DiaSemana, HoraDesde, MinutosDesde, Observacion }
  actualizarHorarioMedico: (data) => api.put('/Medicos/ActualizarHorarioMedico', data),
  eliminarHorarioMedico: (id) => api.delete(`/Medicos/EliminarHorarioMedico/${id}`),
  // Resuelve técnico (obligatorio) y médico (opcional) disponibles para el turno.
  // idTipoEstudio es opcional: si se envía, el técnico además debe poder realizar ese estudio.
  resolverAsignacion: (idCentro, fecha, hora, idTipoEstudio) => api.get('/Medicos/ResolverAsignacion', { params: { idCentro, fecha, hora, ...(idTipoEstudio != null && { idTipoEstudio }) } }),
  // Indica si el centro tiene disponibilidad (según técnicos); hora es opcional
  disponibilidadCentro: (idCentro, fecha, hora) => api.get('/Medicos/DisponibilidadCentro', { params: { idCentro, fecha, ...(hora && { hora }) } }),
  // Disponibilidad semanal de un profesional en un centro: devuelve los 7 días
  // { fecha, diaSemana, atiende, deshabilitado }. fecha en formato yyyy-MM-dd (cualquier día de la semana).
  obtenerSemanaProfesional: (idMedico, idCentro, fecha) => api.get('/Medicos/ObtenerSemanaProfesional', { params: { idMedico, idCentro, fecha } }),
  // Deshabilita un día puntual del profesional en el centro (excepción por fecha).
  deshabilitarDia: (idMedico, idCentro, fecha, motivo = null) => api.post('/Medicos/DeshabilitarDia', { idMedico, idCentro, fecha, motivo }),
  // Rehabilita un día previamente deshabilitado.
  rehabilitarDia: (idMedico, idCentro, fecha) => api.post('/Medicos/RehabilitarDia', { idMedico, idCentro, fecha }),
};

export const derivadoresAPI = {
  getDerivadores: () => api.get('/Derivadores/ListarDerivadores'),
  buscarDerivadores: (nombre, especialidad) => {
    const params = new URLSearchParams();
    if (nombre) params.append('nombre', nombre);
    if (especialidad) params.append('especialidad', especialidad);
    return api.get(`/Derivadores/Buscar?${params.toString()}`);
  },
  getDerivadorById: (idDerivador) => api.get(`/Derivadores/${idDerivador}`),
  agregarDerivador: (data) => api.post('/Derivadores', data),
  guardar: (data) => api.post('/Derivadores', data),
  actualizar: (id, data) => api.put(`/Derivadores/${id}`, data),
  eliminar: (id) => api.delete(`/Derivadores/${id}`),
};

export const equiposAPI = {
  getEquipos: (idCentro) => api.get('/Equipos/ListarEquipos', { params: idCentro ? { idCentro } : {} }),
  getEquiposTomados: (fechaDesde, fechaHasta, idEquipo = null) => {
    const params = { fechaDesde, fechaHasta };
    if (idEquipo) params.idEquipo = idEquipo;
    return api.get('/Turnos/EquiposTomados', { params });
  },
  guardar: (data) => api.post('/Equipos', data),
  actualizar: (id, data) => api.put(`/Equipos/${id}`, data),
  eliminar: (id) => api.delete(`/Equipos/${id}`),
};

export const practicasAPI = {
  guardarPractica: (data) => api.post('/PracticasPSG/GuardarPractica', data),
  generarInforme: (idTurno, reportType) => api.get(`/Reports/generate/${idTurno}/${reportType}`, { responseType: 'blob' }),
  convertirDocxAPdf: (docxBlob, fileName) => {
    const formData = new FormData();
    formData.append('documentFile', docxBlob, fileName);
    return api.post('/Converts/ConvertDocuments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    });
  },
  obtenerPractica: (idTurno) => api.get(`/PracticasPSG/ObtenerPractica/${idTurno}`),
  eliminarPractica: (idTurno) => api.delete(`/PracticasPSG/EliminarPractica/${idTurno}`),
};

export const adjuntosAPI = {
  uploadFile: (file, idTurno) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('idTurno', idTurno);
    return api.post('/Adjuntos/Upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAdjuntos: (idTurno, type) => api.get(`/Adjuntos/ObtenerPorTurno/${idTurno}${type ? `?type=${type}` : ''}`),
  deleteAdjunto: (idAdjunto) => api.delete(`/Adjuntos/Eliminar/${idAdjunto}`),
  getPublicUrl: (nombreAdjunto) => `${API_CONFIG.ADJUNTOS_URL}/${nombreAdjunto}`,
  descargarEeg: (idAdjunto) => api.get(`/Adjuntos/DescargarEeg/${idAdjunto}`),
};

export const observacionesAPI = {
  getObservaciones: (idTurno) => api.get(`/Observaciones/${idTurno}`),
  guardarObservacion: (data) => api.post('/Observaciones', data),
};

export const categoriasAPI = {
  getCategorias: () => api.get('/Categorias'),
  guardarCategoria: (categoria) => api.post('/Categorias', { Categoria: categoria }),
  actualizarCategoria: (id, categoria) => api.put(`/Categorias/${id}`, { Categoria: categoria }),
  eliminarCategoria: (id) => api.delete(`/Categorias/${id}`),
};

export const obrasSocialesAPI = {
  getObrasSociales: () => api.get('/ObrasSociales/ObtenerObrasSociales'),
  getPlanesPorObraSocial: (idObraSocial) => api.get(`/ObrasSociales/ObtenerPlanesPorObraSocial?idObraSocial=${idObraSocial}`),
  guardarObraSocial: (data) => api.post('/ObrasSociales/GuardarObraSocial', data),
  actualizarObraSocial: (id, data) => api.put(`/ObrasSociales/ActualizarObraSocial/${id}`, data),
  eliminarObraSocial: (id) => api.delete(`/ObrasSociales/EliminarObraSocial/${id}`),
  guardarPlan: (data) => api.post('/ObrasSociales/GuardarPlan', data),
  actualizarPlan: (idPlan, data) => api.put(`/ObrasSociales/ActualizarPlan/${idPlan}`, data),
  eliminarPlan: (idPlan) => api.delete(`/ObrasSociales/EliminarPlan/${idPlan}`),
  subirAdjuntos: (idObraSocial, formData) => api.post(`/ObrasSociales/SubirAdjuntos/${idObraSocial}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  eliminarAdjunto: (idObraSocial, idx) => api.delete(`/ObrasSociales/EliminarAdjunto/${idObraSocial}/${idx}`),
  actualizarImportesFacturacion: () => api.get('/Billing/BillingSocialWork'),
  getAdjuntosPorObraSocial: (idObraSocial) => api.get('/ObrasSociales/ObtenerAdjuntosPorObraSocial', { params: { idObraSocial } }),
  getRutasGuardado: () => api.get('/ObrasSociales/ObtenerRutasGuardado'),
  guardarRutaGuardado: (data) => api.post('/ObrasSociales/GuardarRutaGuardado', data),
  actualizarRutaGuardado: (id, data) => api.put(`/ObrasSociales/ActualizarRutaGuardado/${id}`, data),
  eliminarRutaGuardado: (id) => api.delete(`/ObrasSociales/EliminarRutaGuardado/${id}`),
  listarRequisitos: (params) => api.get('/ObrasSociales/ListarRequisitos', { params }),
  guardarRequisitos: (data) => api.post('/ObrasSociales/GuardarRequisitos', data),
  actualizarRequisito: (id, data) => api.put(`/ObrasSociales/ActualizarRequisito/${id}`, data),
  eliminarRequisito: (id) => api.delete(`/ObrasSociales/EliminarRequisito/${id}`),
  getRequisitos: () => api.get('/ObrasSociales/ObtenerRequisitos'),
  guardarRequisito: (requisito) => api.post(`/ObrasSociales/GuardarRequisito?requisito=${encodeURIComponent(requisito)}`),
  actualizarRequisitoCatalogo: (id, requisito) => api.put(`/ObrasSociales/ActualizarRequisitoCatalogo/${id}?requisito=${encodeURIComponent(requisito)}`),
  eliminarRequisitoCatalogo: (id) => api.delete(`/ObrasSociales/EliminarRequisitoCatalogo/${id}`),
  getCentrosPorObraSocial: (idObraSocial) => api.get('/ObrasSociales/ObtenerCentrosPorObraSocial', { params: { idObraSocial } }),
  guardarCentrosObraSocial: (idObraSocial, idsCentros) => api.post(`/ObrasSociales/GuardarCentrosObraSocial/${idObraSocial}`, { idsCentros }),
};

export const chatAPI = {
  getPendientes: () => api.get('/Chat/Pendientes'),
  asignarOperador: (data) => api.post('/Chat/AsignarOperador', data),
  enviarMensaje: (data) => api.post('/Chat/EnviarMensaje', data),
  getMensajes: (solicitudId) => api.get(`/Chat/ObtenerMensajes/${solicitudId}`),
  finalizar: (solicitudId) => api.post(`/Chat/Finalizar/${solicitudId}`),
};

export const trazabilidadAPI = {
  getLogs: (params) => api.get('/Trazabilidad', { params }),
  getUsuarios: (resource) => api.get('/Trazabilidad/Usuarios', { params: resource ? { resource } : {} }),
};

export const tokensAPI = {
  enviarToken: (token) => api.post('/DropBox/GenerateToken', { token }),
};

export const localidadesAPI = {
  getLocalidades: () => api.get('/Localidades/ObtenerLocalidades'),
  guardarLocalidad: (localidad) => api.post('/Localidades', { Localidad: localidad }),
  actualizarLocalidad: (id, localidad) => api.put(`/Localidades/${id}`, { Localidad: localidad }),
  eliminarLocalidad: (id) => api.delete(`/Localidades/${id}`),
};

export const personasAPI = {
  getPersonaPorDni: (dni) => api.get(`/Personas/ObtenerPersonaPorDni?dni=${dni}`),
  buscarPacientes: (buscar) => api.get('/Paciente/Buscar', { params: { buscar } }),
  getTurnosPorPersona: (idPersona) => api.get(`/Paciente/Turnos/${idPersona}`),
};

export const importesPersonalAPI = {
  getImportes: (idTipoEstudio, idPersona, esTecnico) => api.get('/ImportesPersonal', { params: { idTipoEstudio, idPersona, esTecnico } }),
  getPersonal: (esTecnico) => api.get('/ImportesPersonal/Personal', { params: { esTecnico } }),
  guardar: (data) => api.post('/ImportesPersonal', data),
  actualizar: (id, data) => api.put(`/ImportesPersonal/${id}`, data),
  eliminar: (id, esTecnico) => api.delete(`/ImportesPersonal/${id}`, { params: { esTecnico } }),
};

export const pagoPorEstudioAPI = {
  getPagos: (idTipoEstudio, esTecnico) => api.get('/PagoPorEstudio', { params: { idTipoEstudio, esTecnico } }),
  getTiposEstudios: () => api.get('/TiposEstudios/ObtenerTiposEstudios'),
  getCentros: () => api.get('/Centros/ObtenerCentros'),
  guardar: (data) => api.post('/PagoPorEstudio', data),
  actualizar: (id, data) => api.put(`/PagoPorEstudio/${id}`, data),
  eliminar: (id) => api.delete(`/PagoPorEstudio/${id}`),
};

export const coloresEstudiosAPI = {
  getColores: () => api.get('/ColoresEstudios'),
  guardar: (data) => api.post('/ColoresEstudios', data),
  actualizar: (id, data) => api.put(`/ColoresEstudios/${id}`, data),
  eliminar: (id) => api.delete(`/ColoresEstudios/${id}`),
};

export const cmsAPI = {
  getPublicado: (slot, audiencia) => api.get('/Cms/Publicado', { params: { slot, audiencia } }),
  getContenidos: (filtros) => api.get('/Cms', { params: filtros }),
  getById: (id) => api.get(`/Cms/${id}`),
  guardar: (data) => api.post('/Cms', data),
  actualizar: (id, data) => api.put(`/Cms/${id}`, data),
  eliminar: (id) => api.delete(`/Cms/${id}`),
  subirMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/Cms/Media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const usuariosAPI = {
  getUsuarios: (usuario, idRol) => api.get('/Usuarios', { params: { usuario, idRol } }),
  getRoles: () => api.get('/Usuarios/Roles'),
  guardar: (data) => api.post('/Usuarios', data),
  actualizar: (id, data) => api.put(`/Usuarios/${id}`, data),
  cambiarEstado: (id, activo) => api.patch(`/Usuarios/${id}/estado`, { Activo: activo }),
  eliminar: (id) => api.delete(`/Usuarios/${id}`),
};

export const conveniosAPI = {
  getConvenios: (idObraSocial) => api.get('/Convenios', { params: idObraSocial ? { idObraSocial } : {} }),
  getLog: (id) => api.get(`/Convenios/Log/${id}`),
  guardar: (data) => api.post('/Convenios', data),
  actualizar: (id, data) => api.put(`/Convenios/${id}`, data),
  eliminar: (id) => api.delete(`/Convenios/${id}`),
  aumentoGeneral: (data) => api.post('/Convenios/AumentoGeneral', data),
};

export const estudiosMedicosAPI = {
  getTurnos: (params) => api.get('/EstudiosMedicos', { params }),
  getTurno: (idHorario) => api.get(`/EstudiosMedicos/${idHorario}`),
  getConsultasAnteriores: (idHorario, idPersona, idTurno) =>
    api.get(`/EstudiosMedicos/${idHorario}/ConsultasAnteriores`, { params: { idPersona, idTurno } }),
  guardarHistoria: (idTurno, data) => api.post(`/EstudiosMedicos/${idTurno}/Historia`, data),
};

export const resumenAPI = {
  getResumen: (params) => api.get('/Resumen', { params }),
};

export const turnosRequisitosAPI = {
  getByTurno: (idTurno) => api.get(`/TurnosRequisitos/${idTurno}`),
  getByObraOrPlan: (idObraSocial, idRequisito, idTipoEstudio, idPlan) => 
    api.get('/TurnosRequisitos/requisitos', { 
      params: { idObraSocial, idRequisito, idTipoEstudio, idPlan } 
    }),
};

export const notificacionesWPAPI = {
  listar: () => api.get('/NotificacionesWP'),
  crear: (data) => api.post('/NotificacionesWP', data),
  actualizar: (id, data) => api.put(`/NotificacionesWP/${id}`, data),
  eliminar: (id) => api.delete(`/NotificacionesWP/${id}`),
};

export const encuestasAPI = {
  getRespuestas: (page = 1, limit = 50, startDate, endDate) => {
    const params = { page, limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get('/Encuesta/Respuestas', { params });
  },
  getRespuesta: (submissionId) => api.get(`/Encuesta/Respuestas/${submissionId}`),
  desofuscar: (token) => api.get('/Encuesta/Desofuscar', { params: { token } }),
  getResumen: (regenerar = false) => api.get('/Encuesta/Resumen', { params: { regenerar } }),
};
 
export default api;   